/* Services & area: what you offer, and where.
 *
 * A request reaches you only for a service you switch on, at an address inside
 * one of your areas (a centre point and a radius). Every type of a service is
 * offered — every ambulance type, for example — and the platform still checks
 * your documents before you are sent anything. Saving replaces both lists
 * (PUT /agency/coverage). */

import { useEffect, useState } from 'react';
import { getCoverage, saveCoverage } from '../data/agency';
import { serviceName } from '../data/words';
import { canManage } from '../nav';
import { useProfile } from '../shell/profile';
import { Btn, Card, CardHead, Empty, Note, Stack, Table, Toggle, TRow, uiCss } from '../ui';
import { ServiceDot, useAsync, useNotice } from './common';

interface AreaDraft {
  key: number;
  label: string;
  lat: string;
  lng: string;
  radius_km: string;
}

let nextKey = 1;

const MEANING: Record<string, string> = {
  NURSE: 'Nurses and attendants at home, by the shift.',
  HOME_ICU: 'A nurse team and ICU care set up at home.',
  PHYSIO: 'Physiotherapy visits at home.',
  AMBULANCE: 'Every ambulance type: patient transport, basic and advanced life support, ICU. Advanced and ICU trips come only to agencies — you send a paramedic.',
  DOCTOR: 'Doctor consultations, at the platform price. Only agencies are offered them; the first to accept takes the consultation.',
};

const AREA_COLS = '1.6fr 1fr 1fr 0.7fr 1.6fr';

/* A browser gives a page the device's location only over https (or on
 * localhost); over plain http getCurrentPosition fails at once. So "Where I
 * am" tries the device where it can and otherwise finds the place by name, by
 * a pasted Google Maps link, or by "lat, lng" (found on the server 2026-10-27:
 * the button did nothing on http://…:8085). */
const CAN_LOCATE = typeof window !== 'undefined' && window.isSecureContext && !!navigator.geolocation;

interface Place {
  name: string;
  lat: string;
  lng: string;
}

const NUM = '(-?\\d{1,3}(?:\\.\\d+)?)';

// Coordinates written in what was typed or pasted: a place's pin in a Google
// Maps link (!3d…!4d…), its q= / ll= / query=, the map's @centre, or a bare
// "26.2760, 73.0130". Null when there are none.
function coordsIn(text: string): { lat: string; lng: string } | null {
  const t = decodeURIComponentSafe(text.trim());
  const tries = [
    new RegExp(`!3d${NUM}!4d${NUM}`),
    new RegExp(`[?&](?:q|ll|query|destination|center)=${NUM}\\s*,\\s*${NUM}`),
    new RegExp(`@${NUM},${NUM}`),
    new RegExp(`^${NUM}\\s*[, ]\\s*${NUM}$`),
  ];
  for (const re of tries) {
    const m = t.match(re);
    if (!m) continue;
    const lat = Number(m[1]);
    const lng = Number(m[2]);
    if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat: lat.toFixed(6), lng: lng.toFixed(6) };
  }
  return null;
}

function decodeURIComponentSafe(s: string) {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

// Places in India matching a name, from OpenStreetMap's public search (only
// the words typed are sent). The city is added when the words do not name it.
async function searchPlaces(text: string, city: string): Promise<Place[]> {
  const q = city && !text.toLowerCase().includes(city.toLowerCase()) ? `${text}, ${city}` : text;
  const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&countrycodes=in&q=${encodeURIComponent(q)}`;
  const r = await fetch(url, { headers: { 'Accept-Language': 'en' } });
  if (!r.ok) throw new Error(`search answered ${r.status}`);
  const rows = (await r.json()) as { display_name: string; lat: string; lon: string }[];
  return rows.map((p) => ({ name: p.display_name, lat: Number(p.lat).toFixed(6), lng: Number(p.lon).toFixed(6) }));
}

export function CoverageScreen() {
  const { profile, reload: reloadProfile } = useProfile();
  const manage = canManage(profile.me.role);
  const cov = useAsync(getCoverage, []);
  const { notice, setNotice, run } = useNotice();
  const [services, setServices] = useState<Set<string>>(new Set());
  const [areas, setAreas] = useState<AreaDraft[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cov.data || dirty) return;
    setServices(new Set(cov.data.services.filter((s) => s.active).map((s) => s.code)));
    setAreas(cov.data.areas.map((a) => ({ key: nextKey++, label: a.label, lat: String(a.lat), lng: String(a.lng), radius_km: String(a.radius_km) })));
  }, [cov.data, dirty]);

  const toggle = (code: string) => {
    setDirty(true);
    setServices((prev) => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code);
      else n.add(code);
      return n;
    });
  };

  const edit = (key: number, k: keyof Omit<AreaDraft, 'key'>, v: string) => {
    setDirty(true);
    setAreas((prev) => prev.map((a) => (a.key === key ? { ...a, [k]: v } : a)));
  };

  const [finding, setFinding] = useState<number | null>(null);
  const [findText, setFindText] = useState('');
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [findState, setFindState] = useState<{ busy?: boolean; error?: string }>({});
  const [locating, setLocating] = useState<number | null>(null);
  const city = profile.agency.city_name || '';

  const openFinder = (key: number, why?: string) => {
    setFinding(key);
    setFindText('');
    setPlaces(null);
    setFindState(why ? { error: why } : {});
  };

  const put = (key: number, lat: string, lng: string, name?: string) => {
    edit(key, 'lat', lat);
    edit(key, 'lng', lng);
    if (name) {
      setAreas((prev) => prev.map((a) => (a.key === key && !a.label.trim() ? { ...a, label: (name.split(',')[0] ?? name).trim() } : a)));
    }
    setFinding(null);
    setNotice({ tone: 'ok', text: 'Place set. Check it with “Map”, then save.' });
  };

  const hereFor = (key: number) => {
    if (!CAN_LOCATE) {
      openFinder(key);
      return;
    }
    setLocating(key);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLocating(null);
        put(key, p.coords.latitude.toFixed(6), p.coords.longitude.toFixed(6));
      },
      (e) => {
        setLocating(null);
        openFinder(
          key,
          e.code === e.PERMISSION_DENIED
            ? 'The browser was not allowed to share your location. Find the place by name instead.'
            : 'Your location could not be read. Find the place by name instead.',
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  };

  const find = async () => {
    if (finding == null || !findText.trim()) return;
    const c = coordsIn(findText);
    if (c) return put(finding, c.lat, c.lng);
    if (/^https?:\/\//i.test(findText.trim())) {
      setFindState({ error: 'That link has no coordinates in it (a short maps.app.goo.gl link hides them). Open it, then copy the long link from the address bar — or type the place name.' });
      return;
    }
    setFindState({ busy: true });
    try {
      const found = await searchPlaces(findText.trim(), city);
      setPlaces(found);
      setFindState(found.length ? {} : { error: 'No place found by that name. Try a nearby landmark, area or PIN code.' });
    } catch {
      setPlaces(null);
      setFindState({ error: 'The place search could not be reached. Paste a Google Maps link or the latitude, longitude instead.' });
    }
  };

  const save = async () => {
    const parsed: { label: string; lat: number; lng: number; radius_km: number }[] = [];
    for (const a of areas) {
      const lat = Number(a.lat);
      const lng = Number(a.lng);
      const r = Number(a.radius_km);
      if (!a.label.trim()) return setNotice({ tone: 'danger', text: 'Give every area a name.' });
      if (!a.lat.trim() || !a.lng.trim() || !Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lng) || lng < -180 || lng > 180)
        return setNotice({ tone: 'danger', text: `“${a.label}”: the latitude and longitude are not valid.` });
      if (!Number.isFinite(r) || r < 1 || r > 100) return setNotice({ tone: 'danger', text: `“${a.label}”: the radius must be between 1 and 100 km.` });
      parsed.push({ label: a.label.trim(), lat, lng, radius_km: r });
    }
    setSaving(true);
    const ok = await run(() => saveCoverage(Array.from(services), parsed), 'Saved. Requests now follow these services and areas.');
    setSaving(false);
    if (ok) {
      setDirty(false);
      cov.reload();
      void reloadProfile();
    }
  };

  const list = cov.data?.services ?? [];

  return (
    <Stack>
      {notice}
      {cov.error ? <Note tone="danger">Your services and area could not be read: {cov.error}</Note> : null}
      {!manage ? <Note tone="amber">Only an owner or admin can change these. You can read them.</Note> : null}
      {dirty && manage ? (
        <Note tone="amber">
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ flex: 1 }}>You have changes that are not saved yet.</span>
            <Btn size="sm" onClick={() => setDirty(false)} disabled={saving}>
              Undo
            </Btn>
            <Btn size="sm" variant="primary" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Btn>
          </span>
        </Note>
      ) : null}

      <Card>
        <CardHead
          title="Services you offer"
          sub="A request reaches you only for a service switched on here."
          right={<span className={uiCss.sub}>{services.size} switched on</span>}
        />
        {cov.loading && !cov.data ? (
          <Empty>Reading your services…</Empty>
        ) : list.length === 0 ? (
          <Empty>No services are listed.</Empty>
        ) : (
          <div>
            {list.map((sv) => (
              <div key={sv.code} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderTop: '1px solid var(--line-soft)' }}>
                <span className={uiCss.grow}>
                  <span style={{ display: 'block', font: '700 13.5px var(--font-ui)', color: 'var(--ink)' }}>
                    <ServiceDot code={sv.code} />
                    {sv.name || serviceName(sv.code)}
                  </span>
                  <span className={uiCss.sub}>{MEANING[sv.code] ?? ''}</span>
                </span>
                {manage ? (
                  <Toggle on={services.has(sv.code)} onClick={() => toggle(sv.code)} label={`Offer ${sv.name || serviceName(sv.code)}`} />
                ) : (
                  <span className={uiCss.sub}>{services.has(sv.code) ? 'On' : 'Off'}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <CardHead
          title="Areas you cover"
          sub={`In ${profile.agency.city_name || profile.agency.city_code || 'your city'}. A centre point and how far from it you go — a request inside any of them reaches you.`}
          right={
            manage ? (
              <Btn
                size="sm"
                onClick={() => {
                  setDirty(true);
                  setAreas((prev) => [...prev, { key: nextKey++, label: '', lat: '', lng: '', radius_km: '10' }]);
                }}
              >
                Add an area
              </Btn>
            ) : null
          }
        />
        {areas.length === 0 ? (
          <Empty>No areas yet. Without one, no request reaches you.</Empty>
        ) : (
          <Table cols={AREA_COLS} head={['Name', 'Latitude', 'Longitude', 'Radius (km)', '']} minWidth={760}>
            {areas.map((a) => (
              <TRow key={a.key} cols={AREA_COLS}>
                <input
                  className={uiCss.input}
                  disabled={!manage}
                  value={a.label}
                  placeholder="Main hospital"
                  aria-label="Area name"
                  onChange={(e) => edit(a.key, 'label', e.target.value)}
                />
                <input className={uiCss.input} disabled={!manage} value={a.lat} inputMode="decimal" aria-label="Latitude" onChange={(e) => edit(a.key, 'lat', e.target.value)} />
                <input className={uiCss.input} disabled={!manage} value={a.lng} inputMode="decimal" aria-label="Longitude" onChange={(e) => edit(a.key, 'lng', e.target.value)} />
                <input
                  className={uiCss.input}
                  disabled={!manage}
                  value={a.radius_km}
                  inputMode="decimal"
                  aria-label="Radius in km"
                  onChange={(e) => edit(a.key, 'radius_km', e.target.value)}
                />
                <span style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {manage ? (
                    <Btn size="xs" onClick={() => hereFor(a.key)} disabled={locating === a.key}>
                      {locating === a.key ? 'Finding you…' : CAN_LOCATE ? 'Where I am' : 'Find the place'}
                    </Btn>
                  ) : null}
                  {a.lat && a.lng ? (
                    <a
                      className={uiCss.sub}
                      style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}
                      href={`https://www.google.com/maps?q=${encodeURIComponent(`${a.lat},${a.lng}`)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Map
                    </a>
                  ) : null}
                  {manage ? (
                    <Btn
                      size="xs"
                      variant="danger"
                      onClick={() => {
                        setDirty(true);
                        setAreas((prev) => prev.filter((x) => x.key !== a.key));
                      }}
                    >
                      Remove
                    </Btn>
                  ) : null}
                </span>
              </TRow>
            ))}
          </Table>
        )}
        {manage && finding != null && areas.some((a) => a.key === finding) ? (
          <div style={{ padding: '14px 18px', borderTop: '1px solid var(--line-soft)', display: 'grid', gap: 10 }}>
            <span style={{ font: '700 13.5px var(--font-ui)', color: 'var(--ink)' }}>
              Where is “{areas.find((a) => a.key === finding)?.label.trim() || 'this area'}”?
            </span>
            <span className={uiCss.sub}>
              Type a place, area or landmark{city ? ` in ${city}` : ''} — or paste a Google Maps link, or “26.2760, 73.0130”.
            </span>
            <form
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
              onSubmit={(e) => {
                e.preventDefault();
                void find();
              }}
            >
              <input
                className={uiCss.input}
                style={{ flex: '1 1 280px' }}
                autoFocus
                value={findText}
                placeholder={city ? `e.g. Paota, ${city}` : 'e.g. Paota, Jodhpur'}
                aria-label="Place, Google Maps link or latitude, longitude"
                onChange={(e) => setFindText(e.target.value)}
              />
              <Btn size="sm" variant="primary" onClick={() => void find()} disabled={findState.busy || !findText.trim()}>
                {findState.busy ? 'Finding…' : 'Find'}
              </Btn>
              <Btn size="sm" onClick={() => setFinding(null)}>
                Cancel
              </Btn>
            </form>
            {findState.error ? <Note tone="amber">{findState.error}</Note> : null}
            {places && places.length ? (
              <div style={{ display: 'grid', gap: 6 }}>
                {places.map((p) => (
                  <div key={`${p.lat},${p.lng}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--line-soft)', borderRadius: 8 }}>
                    <span className={uiCss.grow} style={{ font: '500 13px var(--font-ui)', color: 'var(--ink)' }}>
                      {p.name}
                    </span>
                    <a
                      className={uiCss.sub}
                      style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}
                      href={`https://www.google.com/maps?q=${encodeURIComponent(`${p.lat},${p.lng}`)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Map
                    </a>
                    <Btn size="xs" variant="primary" onClick={() => put(finding, p.lat, p.lng, p.name)}>
                      Use this
                    </Btn>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {manage ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--line-soft)' }}>
            <Btn variant="primary" onClick={() => void save()} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save services and areas'}
            </Btn>
          </div>
        ) : null}
      </Card>
    </Stack>
  );
}
